import { Quote } from "lucide-react";
import {
  Cite,
  IconList,
  SectionHeader,
  SourcesList,
  type Source,
} from "@/components/insights/article-kit";

export const JAMES_BALDWIN_SLUG = "james-baldwin-witness-mobile-alabama";

const sources: Source[] = [
  {
    n: 1,
    label:
      "Library of Congress — James Baldwin Papers, biographical note (life dates and major works).",
    url: "https://www.loc.gov/item/mm2017085744/",
  },
  {
    n: 2,
    label:
      'BFI / BBC catalog — "Meeting the Man: James Baldwin in Paris" (1970), dir. Terence Dixon.',
    url: "https://www2.bfi.org.uk/films-tv-people/4ce2b94236e22",
  },
  {
    n: 3,
    label:
      "Penguin Random House — Notes of a Native Son (Beacon Press, 1955).",
    url: "https://www.penguinrandomhouse.com/books/162271/notes-of-a-native-son-by-james-baldwin/",
  },
  {
    n: 4,
    label:
      "Penguin Random House — Giovanni's Room (Dial Press, 1956).",
    url: "https://www.penguinrandomhouse.com/books/162267/giovannis-room-by-james-baldwin/",
  },
  {
    n: 5,
    label:
      "Penguin Random House — The Fire Next Time (Dial Press, 1963).",
    url: "https://www.penguinrandomhouse.com/books/162265/the-fire-next-time-by-james-baldwin/",
  },
  {
    n: 6,
    label:
      "Magnolia Pictures — I Am Not Your Negro (dir. Raoul Peck, 2016), based on Baldwin's unfinished manuscript Remember This House.",
    url: "https://www.magpictures.com/iamnotyournegro/",
  },
  {
    n: 7,
    label:
      "National Park Service — Africatown Historic District, Mobile, Alabama (descendants of the Clotilda).",
    url: "https://www.nps.gov/places/africatown-historic-district.htm",
  },
  {
    n: 8,
    label:
      "History Museum of Mobile — Clotilda: The Exhibition (ongoing).",
    url: "https://historymuseumofmobile.com/clotilda-exhibition/",
  },
  {
    n: 9,
    label:
      "University of South Alabama — Department of English & African American Studies course offerings.",
    url: "https://www.southalabama.edu/colleges/artsandsci/english/",
  },
  {
    n: 10,
    label:
      "Bishop State Community College — Humanities & Social Sciences Division.",
    url: "https://www.bishop.edu/academics/programs/",
  },
  {
    n: 11,
    label:
      "Prism United — LGBTQ+ youth services in Mobile and Baldwin Counties, Alabama.",
    url: "https://www.prismunited.org/",
  },
  {
    n: 12,
    label:
      "Mobile Public Library — Local History & Genealogy programming.",
    url: "https://www.mplonline.org/local-history",
  },
];

const JamesBaldwinArticle = () => {
  return (
    <div className="space-y-12 text-foreground">
      {/* Lead */}
      <section className="space-y-4">
        <p className="text-lg leading-relaxed text-muted-foreground">
          James Baldwin (1924–1987)<Cite ns={[1]} /> was a novelist, essayist, and
          public witness whose work named what most of his country preferred not to
          see — the shape of American racism, the cost of denial, and the unfinished
          business of love. We invoke him at the top of Pride Month not as
          decoration, but because his posture is the posture our firm tries to keep:
          tell the truth, stay in the room, and represent the people whose lives are
          being decided about.
        </p>
      </section>

      {/* Hero quote */}
      <section className="bg-card border border-border rounded-2xl p-6 md:p-8">
        <Quote className="h-5 w-5 text-primary mb-3" />
        <blockquote className="font-display text-2xl md:text-3xl leading-snug text-foreground italic">
          "The world is held together…by the love and the passion of a very few
          people. Otherwise, of course, you can despair. Look around."
        </blockquote>
        <p className="text-xs text-muted-foreground mt-4">
          — James Baldwin, in <em>Meeting the Man: James Baldwin in Paris</em>
          <Cite ns={[2]} /> (dir. Terence Dixon, BBC, 1970)
        </p>
      </section>

      {/* Who he was */}
      <section>
        <SectionHeader
          kicker="Who he was"
          title="A Black, queer American who refused to look away"
          description="Born in Harlem in 1924, Baldwin spent much of his adult life in France, returning often to bear witness to the U.S. civil rights movement and to the lives — including his own — that his country tried to render invisible."
        />
        <div className="prose max-w-none prose-p:text-muted-foreground prose-p:leading-relaxed prose-strong:text-foreground">
          <p>
            Baldwin published his first novel, <em>Go Tell It on the Mountain</em>,
            in 1953, and over the next three decades produced essays, novels, and
            plays that became foundational texts for civil rights organizers,
            literary scholars, and LGBTQ+ readers alike.<Cite ns={[1]} /> He wrote
            openly about same-sex love at a time when very few Black American writers
            were given room to do so — most directly in <em>Giovanni's Room</em>
            (1956).<Cite ns={[4]} />
          </p>
        </div>
      </section>

      {/* The witness */}
      <section>
        <SectionHeader
          kicker="The witness"
          title="Four works that still do the work"
        />
        <IconList
          variant="arrow"
          items={[
            {
              lead: "Notes of a Native Son (1955).",
              body: (
                <>
                  Ten essays that taught a generation how to read American race as a
                  shared inheritance rather than a private grievance.<Cite ns={[3]} />
                </>
              ),
            },
            {
              lead: "Giovanni's Room (1956).",
              body: (
                <>
                  A novel about love between men — published when both his Black
                  readers and his white publishers wanted him to write something else
                  — and now a touchstone of queer literature.<Cite ns={[4]} />
                </>
              ),
            },
            {
              lead: "The Fire Next Time (1963).",
              body: (
                <>
                  Two essays addressed to his nephew and to the country, written on
                  the eve of the Civil Rights Act, still quoted in courtrooms,
                  pulpits, and policy memos.<Cite ns={[5]} />
                </>
              ),
            },
            {
              lead: "I Am Not Your Negro (2016).",
              body: (
                <>
                  Raoul Peck's film, built from Baldwin's unfinished manuscript
                  <em> Remember This House</em>, brought his voice into a new
                  generation's conversation about race and policing.<Cite ns={[6]} />
                </>
              ),
            },
          ]}
        />
      </section>

      {/* Mobile influence */}
      <section>
        <SectionHeader
          kicker="Mobile, Alabama"
          title="Where his witness still shows up here"
          description="Baldwin never made Mobile a subject of his writing, but the work he gave the country lives inside specific Mobile institutions and movements."
        />
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="font-display font-bold mb-2">
              Africatown and the Clotilda
              <Cite ns={[7, 8]} />
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The descendants of the survivors of the <em>Clotilda</em> — the last
              known slave ship to reach the United States — built Africatown in
              Mobile. The community's ongoing work to name, preserve, and tell that
              history is the kind of public witness Baldwin spent his life arguing
              for.
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="font-display font-bold mb-2">
              University of South Alabama & Bishop State
              <Cite ns={[9, 10]} />
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              English and humanities programs at the University of South Alabama and
              Bishop State Community College keep Baldwin's essays and novels in
              rotation in literature, African American Studies, and rhetoric
              classrooms across the city.
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="font-display font-bold mb-2">
              Prism United & LGBTQ+ youth
              <Cite ns={[11]} />
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Baldwin gave Black queer young people language for themselves long
              before that was safe. Mobile's Prism United serves LGBTQ+ youth across
              Mobile and Baldwin Counties, carrying that thread forward in
              programming and peer support.
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="font-display font-bold mb-2">
              Mobile Public Library
              <Cite ns={[12]} />
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Local History & Genealogy programming at the Mobile Public Library is
              where Baldwin's writing meets the city's own archive — the everyday
              place a reader in Mobile most often encounters his work.
            </p>
          </div>
        </div>
      </section>

      {/* What this means for our practice */}
      <section>
        <SectionHeader
          kicker="For our practice"
          title="What we take from him"
        />
        <IconList
          variant="check"
          items={[
            {
              lead: "Lobbying.",
              body: "Sit at the table on behalf of people who were not invited, and name what is actually being decided.",
            },
            {
              lead: "Strategic Communication.",
              body: "Refuse the comforting story. Tell the harder, more accurate one in language a reader can carry.",
            },
            {
              lead: "Advocacy.",
              body: "Treat queer, Black, and small-business Alabamians as the protagonists of their own policy story, not a footnote.",
            },
          ]}
        />
      </section>

      <SourcesList sources={sources} />
    </div>
  );
};

export default JamesBaldwinArticle;